/**
 * lifecycle.ts
 *
 * Canonical type definitions for Sprint 8 lifecycle entities:
 * assignments, voting, assessments (IRL & defensibility), team, outreach.
 *
 * These types are re-exported in web/src/types/index.ts for frontend consumption.
 * Single source of truth — must match 1:1 with api/src/db/schema/lifecycle.ts columns.
 */

// ─── Assignment Types ───────────────────────────────────────────────────────

export type AssignmentRole = 'OWNER' | 'VENTURE_ARCHITECT' | 'SUPPORT' | 'EXTERNAL'

export interface Assignment {
  id: number
  project_id: number
  user_id: number | null
  role: AssignmentRole
  display_name: string
  role_title: string | null
  avatar_url: string | null
  deadline: string | null
  created_at: string
}

// ─── Voting Types ───────────────────────────────────────────────────────────

export type RoundType = 'IDA_REVIEW' | 'VB_RECOMMENDATION_TEAM' | 'VB_RECOMMENDATION_EXEC'
export type RoundStatus = 'OPEN' | 'CLOSED'
export type VoteValue = 'PROMOTE' | 'HOLD' | 'REJECT'
export type RoundResult = 'PROMOTE' | 'HOLD' | 'REJECT' | null

export interface VoteRound {
  id: number
  project_id: number
  round_type: RoundType
  status: RoundStatus
  created_by: number | null
  created_at: string
  closed_at: string | null
  result: RoundResult
  votes?: ProjectVote[] // populated on GET
}

export interface ProjectVote {
  id: number
  round_id: number
  voter_id: number | null
  voter_name: string
  voter_role: string | null
  vote: VoteValue
  comment: string | null
  voted_at: string
}

// ─── IRL Assessment Types ───────────────────────────────────────────────────

export interface IRLAssessment {
  id: number
  project_id: number
  assessed_by: number | null
  trl: number // 0-9
  brl: number // 0-9
  iprrl: number // 0-9
  tmrl: number // 0-9
  frl: number // 0-9
  crl: number // 0-9
  notes: string | null
  assessed_at: string
}

export const IRL_DIMENSIONS = ['trl', 'brl', 'iprrl', 'tmrl', 'frl', 'crl'] as const
export type IRLDimension = typeof IRL_DIMENSIONS[number]

// ─── Defensibility Assessment Types ─────────────────────────────────────────

export interface DefensibilityAssessment {
  id: number
  project_id: number
  assessed_by: number | null
  scale_economies: number // 0-3
  network_effects: number // 0-3
  counter_positioning: number // 0-3
  switching_costs: number // 0-3
  branding: number // 0-3
  cornered_resource: number // 0-3
  process_power: number // 0-3
  notes: string | null
  assessed_at: string
}

export const DEFENSIBILITY_POWERS = [
  'scale_economies',
  'network_effects',
  'counter_positioning',
  'switching_costs',
  'branding',
  'cornered_resource',
  'process_power',
] as const
export type DefensibilityPower = typeof DEFENSIBILITY_POWERS[number]

export const POWER_LABELS: Record<DefensibilityPower, string> = {
  scale_economies: 'Scale Economies',
  network_effects: 'Network Effects',
  counter_positioning: 'Counter-Positioning',
  switching_costs: 'Switching Costs',
  branding: 'Branding',
  cornered_resource: 'Cornered Resource',
  process_power: 'Process Power',
}

// ─── Team Assessment Types ──────────────────────────────────────────────────

export interface TeamAssessment {
  id: number
  project_id: number
  member_name: string
  member_role: string | null
  affiliation: string | null
  assessment: string | null
  assessed_by: number | null
  assessed_at: string
}

// ─── Outreach Event Types ───────────────────────────────────────────────────

export type OutreachEventType = 'EMAIL' | 'CALL' | 'MEETING' | 'SITE_VISIT' | 'CONFERENCE' | 'OTHER'

export interface OutreachEvent {
  id: number
  project_id: number
  event_type: OutreachEventType
  event_date: string | null
  contact_name: string | null
  contact_role: string | null
  notes: string | null
  outcome: string | null
  created_by: number | null
  created_at: string
}

// ─── Tab Types ──────────────────────────────────────────────────────────────

export type LifecycleStage = 'INBOX' | 'LONG_LIST' | 'IN_REVIEW' | 'EXTRA_MILE' | 'VB_PROCESS' | 'FUNDED' | 'ALL'

export interface TabDefinition {
  id: string
  label: string
  stage: LifecycleStage
}
